// Sites CRUD (BUILD_SPEC.md §5.1) — create/list/update only (no `is_active`
// column, no delete — services/api/src/modules/config/sites.ts). ADMIN write;
// read-only for other roles (see permissions.ts).
import { useState } from 'react';
import { Alert, Button, Group, Modal, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import type { SiteCreateInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../permissions';
import { useCreateSite, useSites, useUpdateSite } from '../api';
import type { SiteDto } from '../types';

interface SiteFormValues {
  code: string;
  name: string;
}

function toFormValues(row?: SiteDto): SiteFormValues {
  return { code: row?.code ?? '', name: row?.name ?? '' };
}

function toInput(values: SiteFormValues): SiteCreateInput {
  return { code: values.code.trim(), name: values.name.trim() };
}

function SiteFormModal({ opened, onClose, editing }: { opened: boolean; onClose: () => void; editing: SiteDto | null }) {
  const create = useCreateSite();
  const update = useUpdateSite(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<SiteFormValues>({
    initialValues: toFormValues(editing ?? undefined),
    validate: {
      code: (v) => (v.trim() ? null : 'Code is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
    },
  });

  function handleSubmit(values: SiteFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit site ${editing.code}` : 'New site'} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save site.'}
            </Alert>
          )}
          <TextInput label="Code" required {...form.getInputProps('code')} />
          <TextInput label="Name" required {...form.getInputProps('name')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create site'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function SiteRegister() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, isError } = useSites();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<SiteDto | null>(null);
  const rows = data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: SiteDto) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Sites</Title>
        {isAdmin && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="sm">
            New site
          </Button>
        )}
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load sites.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={420}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>
                    {isAdmin && (
                      <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text c="dimmed" ta="center" py="md">
                      No sites yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <SiteFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />
    </Stack>
  );
}
